import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/landing/Navbar';
import Footer from '../components/landing/Footer';
import { LandingAuthProvider } from '../contexts/LandingAuthContext';
import { LandingAppProvider } from '../contexts/LandingAppContext';
import { TIERS, TIER_ORDER, TIER_RANK, formatStorage } from '../lib/tiers';
import {
  Check, ArrowRight, Loader2, Sparkles, Crown,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tier-accent → tailwind class lookup. Centralised so a designer can
// retune the palette in one spot. Keys match TIERS[tier].accent in lib/tiers.js.
const ACCENTS = {
  slate:  { border: 'border-slate-300',  ring: 'ring-slate-300/30',  pill: 'bg-slate-100  text-slate-700' },
  blue:   { border: 'border-blue-400',   ring: 'ring-blue-400/30',   pill: 'bg-blue-100   text-blue-700'  },
  violet: { border: 'border-violet-500', ring: 'ring-violet-500/30', pill: 'bg-violet-100 text-violet-700'},
  amber:  { border: 'border-amber-500',  ring: 'ring-amber-500/30',  pill: 'bg-amber-100  text-amber-700' },
};

const PricingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isPremium, plan } = useSubscription();
  const [tiers, setTiers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  // iter197 — pricing toggle. 'monthly' is the default to honour the
  // existing $1.99 price-anchor users see in marketing copy; switching
  // to 'annual' multiplies by 10 (2 months free / 17% off).
  const [billingInterval, setBillingInterval] = useState('monthly');

  useEffect(() => {
    // Phase 1 of 4-tier rollout (Feb 2026) — read from the single source
    // of truth on the backend. Fall back to the local mirror in
    // /lib/tiers.js so the page still renders if the backend is briefly
    // unreachable.
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/pricing/tiers`);
        if (!cancelled) setTiers(res.data?.tiers || null);
      } catch (err) {
        console.error('Pricing fetch failed, using local mirror:', err);
        if (!cancelled) {
          setTiers(TIER_ORDER.map((id) => ({
            id,
            name: TIERS[id].name,
            tagline: TIERS[id].tagline,
            price_usd: TIERS[id].priceUsd,
            highlights: TIERS[id].highlights,
          })));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSubscribe = async (tierId, isDowngradeRef) => {
    if (!user) { navigate('/login'); return; }
    // Free is the default — no checkout needed.
    if (tierId === 'free') { navigate('/app'); return; }
    if (isDowngradeRef) {
      const confirmed = window.confirm(
        `You're currently on a higher plan. Switching to ${TIERS[tierId]?.name || tierId} will reduce your access at the end of your current billing cycle. Continue?`
      );
      if (!confirmed) return;
    }
    setProcessingPlan(tierId);
    try {
      const res = await axios.post(`${API}/payments/initialize`, {
        email: user.email,
        plan_id: tierId,
        // iter197 — pricing toggle. Backend reads 'yearly' as the annual
        // path (yearly_total_usd) — keep that string to avoid an API
        // contract change while presenting "annual" to the user.
        interval: billingInterval === 'annual' ? 'yearly' : 'monthly',
        currency: 'USD',
        channels: ['card', 'mobile_money', 'bank'],
      });
      if (res.data?.data?.authorization_url) {
        window.location.href = res.data.data.authorization_url;
      }
    } catch (err) {
      console.error('Payment error:', err);
      // Common cause during Phase 1: Stripe price IDs not configured yet
      // for the new tier. Show a friendly inline message rather than dying.
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'string' && detail.toLowerCase().includes('price')) {
        // eslint-disable-next-line no-alert
        alert("This plan isn't quite ready to subscribe to yet — Stripe products are being set up. Check back shortly.");
      }
    } finally {
      setProcessingPlan(null);
    }
  };

  if (loading) {
    return (
      <LandingAuthProvider>
        <LandingAppProvider>
          <div className="f500-page" data-testid="pricing-loading">
            <Navbar />
            <div className="min-h-[60vh] flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-[var(--f500-ink-3)]" />
            </div>
            <Footer />
          </div>
        </LandingAppProvider>
      </LandingAuthProvider>
    );
  }

  return (
    <LandingAuthProvider>
      <LandingAppProvider>
        <div className="f500-page" data-testid="pricing-page">
          <Navbar />
      {/* ── Hero ───────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-12 lg:pt-20 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <p className="f500-eyebrow mb-6" data-testid="pricing-eyebrow">— Pricing / 2026</p>
            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="f500-display text-5xl sm:text-6xl lg:text-7xl"
            >
              Don't pay for Pro.<br />Earn it.
            </motion.h1>
          </div>
          <div className="lg:col-span-4 lg:pl-8 lg:border-l lg:border-[var(--f500-line)]">
            <p className="f500-body text-base">
              Start free. Complete the <strong>7-day habit loop</strong> — one tiny action each day — and we hand you <strong>a full week of Pro, free</strong>. Most students keep going from there. No card upfront. No tricks.
            </p>
          </div>
        </div>
      </div>

      {/* ── Header strip — show current plan badge if user is logged in ─── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-6">
        <div className="flex flex-wrap items-center justify-between gap-4 border-y border-[var(--f500-line)] py-4">
          <p className="f500-eyebrow">Pick the plan that matches how you study</p>
          {isPremium && (
            <span className="f500-badge f500-badge-blue" data-testid="current-plan-badge">
              <Check className="w-3 h-3" />
              Active — {plan === 'ambassador' ? 'Ambassador Premium' : `${plan?.charAt(0).toUpperCase()}${plan?.slice(1)} Plan`}
            </span>
          )}
        </div>
      </div>

      {/* iter197 — Monthly / Annual toggle. Annual is "2 months free"
          (17% off, industry standard). The toggle drives both the price
          display in each tier card AND the `interval` passed to Paystack
          checkout. Defaults to monthly so the marketing $1.99/mo price
          anchor remains the first thing visitors see. */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-8">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3" data-testid="billing-interval-toggle">
          <div
            className="inline-flex items-center rounded-full bg-[var(--f500-surface-2)] dark:bg-slate-800/70 border border-[var(--f500-line)] p-1"
            role="tablist"
            aria-label="Billing interval"
          >
            <button
              type="button"
              role="tab"
              aria-selected={billingInterval === 'monthly'}
              onClick={() => setBillingInterval('monthly')}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                billingInterval === 'monthly'
                  ? 'bg-[var(--isp-page)] shadow-sm text-[var(--f500-ink-1)]'
                  : 'text-[var(--f500-ink-3)] hover:text-[var(--f500-ink-1)]'
              }`}
              data-testid="billing-monthly-btn"
            >
              Monthly
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={billingInterval === 'annual'}
              onClick={() => setBillingInterval('annual')}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-2 ${
                billingInterval === 'annual'
                  ? 'bg-[var(--isp-page)] shadow-sm text-[var(--f500-ink-1)]'
                  : 'text-[var(--f500-ink-3)] hover:text-[var(--f500-ink-1)]'
              }`}
              data-testid="billing-annual-btn"
            >
              Annual
              <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 text-[10px] font-bold tracking-wide">
                SAVE 17%
              </span>
            </button>
          </div>
          {billingInterval === 'annual' && (
            <span className="text-xs text-emerald-700 font-medium" data-testid="billing-annual-savings-tag">
              ✨ 2 months free · billed once yearly
            </span>
          )}
        </div>
      </div>

      {/* ── 4-tier grid (Free / Student / Scholar / Pro) ───────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pb-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
          {(tiers || []).map((tier, i) => {
            const local = TIERS[tier.id] || {};
            // iter191 — compare ranks so we can label lower tiers
            // "Switch / Downgrade" instead of "Choose Plan" when the
            // user is already on a higher tier (e.g. Pioneer/Pro looking
            // at Scholar). Falls back to "Choose Plan" for unknown ids.
            const currentRank = TIER_RANK[plan] ?? -1;
            const targetRank = TIER_RANK[tier.id] ?? -1;
            const isCurrentPlan = plan === tier.id;
            const isDowngrade = !isCurrentPlan && targetRank !== -1 && currentRank > targetRank;
            const isFree = tier.id === 'free';
            const accent = ACCENTS[local.accent || 'slate'];
            const isRecommended = !!local.recommended;
            const storageMb = local.limits?.storageMb;
            return (
              <motion.div
                key={tier.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08, duration: 0.4 }}
                className="relative"
                data-testid={`plan-card-${tier.id}`}
              >
                {isRecommended && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                    <span className={`inline-flex items-center gap-1 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${accent.pill} rounded-full`}>
                      <Crown className="w-3 h-3" /> Recommended
                    </span>
                  </div>
                )}
                <div
                  className={`f500-card relative h-full p-6 lg:p-7 transition-all flex flex-col ${
                    isRecommended ? `!border-2 ${accent.border} shadow-xl ring-4 ${accent.ring}` : ''
                  }`}
                >
                  {/* Header */}
                  <div className="pb-5 border-b border-[var(--f500-line)]">
                    <h2 className="f500-h1 text-2xl sm:text-3xl mb-1">{tier.name}</h2>
                    <p className="f500-body text-xs leading-snug">{tier.tagline}</p>
                  </div>

                  {/* Price — iter197: switches between monthly and annual.
                      Annual surfaces the per-month-equivalent as the big
                      number with a tiny "billed yearly" footnote, so the
                      visual price-anchor stays small without being
                      misleading. */}
                  <div className="py-5 border-b border-[var(--f500-line)]">
                    {(() => {
                      const showAnnual = billingInterval === 'annual' && !isFree;
                      const displayPrice = showAnnual
                        ? (tier.price_usd_yearly_per_month ?? tier.price_usd)
                        : tier.price_usd;
                      const periodLabel = isFree ? '/ ever' : showAnnual ? '/ mo' : '/ month';
                      return (
                        <>
                          <div className="flex items-baseline gap-1">
                            <span className="f500-mono text-[var(--f500-ink-3)] text-sm">$</span>
                            <span className="f500-display text-4xl sm:text-5xl">
                              {displayPrice === 0 ? '0' : Number(displayPrice).toFixed(2)}
                            </span>
                            <span className="f500-body text-xs">{periodLabel}</span>
                          </div>
                          {showAnnual && (
                            <p className="f500-body text-[11px] mt-1 text-emerald-700 font-medium" data-testid={`annual-bill-${tier.id}`}>
                              ${Number(tier.price_usd_yearly).toFixed(2)} billed yearly · saves ${(tier.price_usd * 12 - tier.price_usd_yearly).toFixed(2)}/yr
                            </p>
                          )}
                          {storageMb !== undefined && (
                            <p className="f500-body text-[11px] mt-1.5 text-[var(--f500-ink-3)]">
                              {formatStorage(storageMb)} storage
                            </p>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  {/* Features */}
                  <ul className="py-5 space-y-2.5 flex-1" data-testid={`plan-highlights-${tier.id}`}>
                    {(tier.highlights || []).map((text, fi) => (
                      <li key={fi} className="flex items-start gap-2">
                        <Check className="w-3.5 h-3.5 text-[var(--f500-ink)] flex-shrink-0 mt-1" />
                        <span className="f500-body text-xs leading-relaxed text-[var(--f500-ink)]">{text}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <div className="mt-auto pt-4">
                    {isCurrentPlan ? (
                      <button disabled className="f500-btn f500-btn-secondary w-full" data-testid={`current-plan-${tier.id}`}>
                        <Check className="w-4 h-4" /> Current Plan
                      </button>
                    ) : isFree ? (
                      <button
                        onClick={() => navigate(user ? '/app' : '/signup')}
                        className="f500-btn f500-btn-secondary w-full"
                        data-testid={`subscribe-${tier.id}`}
                      >
                        {user ? 'Go to App' : 'Get Started Free'} <ArrowRight className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleSubscribe(tier.id, isDowngrade)}
                        disabled={!!processingPlan}
                        className={`f500-btn w-full ${isDowngrade ? 'f500-btn-secondary' : (isRecommended ? 'f500-btn-primary' : 'f500-btn-secondary')}`}
                        data-testid={`subscribe-${tier.id}`}
                        title={isDowngrade ? 'You are currently on a higher plan' : undefined}
                      >
                        {processingPlan === tier.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : isDowngrade ? (
                          <>Switch to {tier.name}</>
                        ) : (
                          <>Choose {tier.name} <ArrowRight className="w-4 h-4" /></>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* ── Free Tier strip — habit-loop bargain ─────────── */}
      <div className="border-y border-[var(--f500-line)] bg-[var(--f500-paper-2)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center" data-testid="free-tier-info">
            <div className="md:col-span-3">
              <p className="f500-eyebrow mb-2">— Free + Habit Loop</p>
              <h3 className="f500-h2 text-2xl">Show up 7 days. Earn 1 week of Pro.</h3>
            </div>
            <div className="md:col-span-7">
              <p className="f500-body text-sm">
                The Free tier is real — transcription, flashcards, focus timer, daily streaks. But every new account also gets a 7-day Habit Loop: one tiny action each day for a week. Finish it and we unlock a full week of <strong>Pro, free</strong>. Build the habit. Decide after.
              </p>
            </div>
            <div className="md:col-span-2 md:text-right">
              <button
                onClick={() => navigate('/app')}
                className="f500-btn f500-btn-ghost"
                data-testid="continue-free-btn"
              >
                Start your habit <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Enterprise / Campus ────────────────────────── */}
      <div className="bg-[var(--f500-ink)] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16 lg:py-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/60 mb-4">— Campus / Enterprise</p>
              <h2 className="f500-h1 text-white text-4xl sm:text-5xl mb-4">
                Deploy iStudyPro<br />for your institution.
              </h2>
              <p className="text-white/70 text-base max-w-2xl" style={{ fontFamily: 'var(--sf-text)' }}>
                Volume pricing starts at $0.99 per user per month. SSO, dedicated success manager, and SOC 2 reporting included.
              </p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <button
                onClick={() => navigate('/campus')}
                className="f500-btn f500-btn-inverse"
                data-testid="campus-plans-cta"
              >
                View Campus Plans <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── On-the-fence nudge — point to the head-on ChatGPT comparison ── */}
      <div className="border-t border-[var(--f500-line)] bg-[var(--f500-paper-2)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center" data-testid="pricing-vs-chatgpt-strip">
            <div className="md:col-span-9">
              <p className="f500-eyebrow mb-2">— Still on the fence?</p>
              <h3 className="f500-h2 text-2xl mb-2">"Can't I just use ChatGPT for studying?"</h3>
              <p className="f500-body text-sm">
                Fair question. We answer it head-on with a 12-row side-by-side — including everything foundation models <strong>structurally can't</strong> ship, no matter how smart the next model gets.
              </p>
            </div>
            <div className="md:col-span-3 md:text-right">
              <Link
                to="/#vs-chatgpt"
                className="f500-btn f500-btn-primary inline-flex items-center gap-2"
                data-testid="pricing-vs-chatgpt-cta"
              >
                Read the comparison <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>

          <Footer />
        </div>
      </LandingAppProvider>
    </LandingAuthProvider>
  );
};

export default PricingPage;
